import React, { Suspense, lazy } from 'react';
/**
 * PortalSwitch — Role-based portal rendering
 *
 * Extracted from App.tsx in R33 (DDD Fase 6 — App Shell decomposition).
 * Renders the appropriate portal shell (Teacher, Parent, Admin) based on
 * the active account's role. Portal visibility is controlled by
 * currentNavView in AppContent — this component only handles which
 * portal shell to render.
 */
import type { AccountsState, Account } from '../accounts';
import type { AppState } from './types';
import type { Question } from '../content';
import type { GroupId } from '../config/systemConfig';
const TeacherShell = lazy(() => import('../portals/teacher-v2/TeacherShellV2').then(m => ({
  default: m.TeacherShellV2
})));
const ParentShell = lazy(() => import('../portals/parent-v2/ParentShellV2').then(m => ({
  default: m.ParentShellV2
})));
const AdminShell = lazy(() => import('../portals/admin/AdminShell').then(m => ({
  default: m.AdminShell
})));
interface PortalSwitchProps {
  activeAccount: Account | null;
  accounts: AccountsState;
  state: AppState;
  selectedGroup: GroupId;
  questions: Question[];
  onNavigateToLearner: (learnerId: string) => void;
  onLogout: () => void;
  onSwitchAccount: (accountId: string) => void;
  onNavigate: (view: string) => void;
  getLearnerData: (learnerId: string) => AppState | null;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
}
const SPINNER_COLORS: Record<string, string> = {
  indigo: 'border-orange-200 border-t-orange-500',
  emerald: 'border-emerald-200 border-t-emerald-600'
};
function PortalSpinner({
  color
}: {
  color: string;
}) {
  const colorClasses = SPINNER_COLORS[color] || SPINNER_COLORS.indigo;
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-799">
      <div className={`w-8 h-8 border-4 rounded-full animate-spin ${colorClasses}`} data-id="element-800" />
    </div>;
}
export function PortalSwitch(props: PortalSwitchProps): React.ReactElement | null {
  const {
    activeAccount,
    accounts,
    state,
    selectedGroup,
    questions,
    onNavigateToLearner,
    onLogout,
    onSwitchAccount,
    onNavigate,
    getLearnerData,
    getLearnerModuleMastery
  } = props;
  const findFirstLearner = () => {
    const firstLearner = Object.values(accounts.accountsById).find(a => a.role === 'learner');
    if (firstLearner) onSwitchAccount(firstLearner.id);
  };
  // Teacher portal
  if (activeAccount?.role === 'teacher') {
    return <Suspense fallback={<PortalSpinner color="indigo" data-id="element-802" />} data-id="element-801">
        <TeacherShell onNavigateToLearner={onNavigateToLearner} onLogout={onLogout} onReturnHome={findFirstLearner} data-id="element-803" />
      </Suspense>;
  }
  // Parent portal
  if (activeAccount?.role === 'parent') {
    return <Suspense fallback={<PortalSpinner color="emerald" data-id="element-805" />} data-id="element-804">
        <ParentShell onNavigateToLearner={onNavigateToLearner} onLogout={onLogout} onReturnHome={findFirstLearner} data-id="element-806" />
      </Suspense>;
  }
  // Admin portal
  if (activeAccount?.role === 'admin') {
    const accountsList = Object.values(accounts.accountsById);
    return <Suspense fallback={<PortalSpinner color="indigo" data-id="element-808" />} data-id="element-807">
        <AdminShell accounts={accounts} getLearnerData={getLearnerData} getLearnerModuleMastery={getLearnerModuleMastery} selectedGroup={selectedGroup} onNavigate={view => onNavigate(view as string)} onNavigateToLearner={onNavigateToLearner} allAccounts={accountsList} activeAccountId={accounts.activeAccountId} onSwitchAccount={onSwitchAccount} onLogout={onLogout} questions={questions} state={state} onReturnHome={() => {
        const firstLearner = accountsList.find(a => a.role === 'learner');
        if (firstLearner) onSwitchAccount(firstLearner.id);
      }} data-id="element-809" />
      </Suspense>;
  }
  return null;
}